import { useEffect, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { matchPath } from "react-router";
import {
  updateRouteNavigate,
  resetRouteNavigate,
} from "core/store/routeStack/actions";
import type { NavigateOptions } from "react-router/dist/lib/context";
import type { To } from "@remix-run/router";
import { useDispatch, useSelector } from "react-redux";
import { config } from "core/config/app";
import { Router } from "core/router";
import { useAuthModal } from "core/context";

const HOME_PATH = config.basename || "/";

export const useRouteHistory = () => {
  const routeHistories = useSelector((state: any) => state.Common.routes);

  const getPrevRoute = useCallback((number: number = -1) => {
    return routeHistories[routeHistories.length + number - 1]?.path || HOME_PATH;
  }, [routeHistories]);

  return { routeHistories, getPrevRoute };
}

export const useNavigateStack = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const routeHistories = useSelector((state: any) => state.Common.routes);

  useEffect(() => {
    let scrollPos = 0;
    const path = `${location.pathname}${location.search || ""}`;
    const index = routeHistories.map((item: any) => item.path).indexOf(path);
    if (index !== -1) {
      scrollPos = routeHistories[index].scrollPos;
    }

    const r: any = Router.find(
      (route: any) => {
        return route?.path !== "*" && matchPath(
          { path: route.path },
          location.pathname,
        )
      }
    )
    // set document title
    if (r?.title) {
      document.title = r.title;
    } else {
      document.title = "Recipe AI";
    }

    // push new route to stack
    dispatch(updateRouteNavigate({ path, scrollPos }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, location.search, updateRouteNavigate]);
};

export const useCustomNavigate = (isRootHistory: boolean = false) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();
  const { openModal, token } = useAuthModal();

  const routeHistories = useSelector((state: any) => state.Common.routes);

  const handleNavigation = useCallback(
    (to: To | number, option?: NavigateOptions, authCheck = true) => {
      const newRouteHistories = [...routeHistories] as any[];
      const currentNavigate = newRouteHistories[newRouteHistories.length - 1];
      if (to === currentNavigate?.path) {
        return;
      }

      if (typeof to === 'string' && authCheck) {
        const match: any = Router.find((route: any) => matchPath((to as string).split("?")[0], route.path));
        if (match?.auth && !token) {
          openModal(() => handleNavigation(to, option, false))
          return;
        }
      }

      // update prev page scroll position to stack
      const path = `${location.pathname}${location.search || ""}`;
      dispatch(updateRouteNavigate({ path, scrollPos: window.scrollY, option }));

      if (typeof to === "number") {
        const prevNavigate = newRouteHistories[newRouteHistories.length - 1 + to];
        // back
        // back in sub page
        if (prevNavigate?.path) {
          to = prevNavigate.path;
        }
        // back in homepage
        else if (currentNavigate?.path === HOME_PATH) {
          // trigger close app in here
          to = HOME_PATH;
        } else {
          // back page to home
          to = HOME_PATH;
          dispatch(resetRouteNavigate());
        }
      }

      if (isRootHistory) {
        // click nav link
        dispatch(resetRouteNavigate());
      }

      return navigate(to as To, option);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    },
    [isRootHistory, routeHistories, token, openModal]
  );

  return handleNavigation;
};

export const useNavigateStackCustom = () => {
  const routeStack = useSelector((state: any) => state.Common.routes);
  // const routeConfig = useSelector(RouteSelectors.routeConfig);

  const getLastRoute = useCallback(
    (isAuthenticated?: boolean): string => {
      if (isAuthenticated === undefined) {
        // guest route
        return routeStack[routeStack.length - 1]?.path || HOME_PATH;
      }

      const routeList = routeStack.filter((item: any) =>
        Router.find(
          (route: any) => {
            return route?.path !== "*" && matchPath(
              { path:route.path },
              item.path,
            ) &&
            (route.auth !== false)
          }
            
        )
      );
      const latRoute = routeList[routeList.length - 1];

      return latRoute?.path || HOME_PATH;
    },
    [routeStack]
  );

  return { routeStack, getLastRoute };
};
